import { Button  } from '@mui/material';
import React, { useState, useEffect  } from 'react';
import { Print } from '@mui/icons-material';
import Loading from 'components/Loading';
import { useMobileDetect, useTabletDetection } from 'containers/chat/hooks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { Button as AntdButton, Modal, Space } from 'antd';

export default function HTMLContentPrinter({ htmlContent, onPrintSingle, printLoader , hasModifiedChange = false, updateContent, getPrintContent }) {
  const showAlert = useAlert();
  const isTablet = useTabletDetection();
  const isMobile = useMobileDetect() && !isTablet;
  const [loader, setLoader] = useState(false);

  const printHTMLContent = (contentOverride) => {
    const contentToPrint = contentOverride !== undefined ? contentOverride : htmlContent;
    if (isMobile || isTablet) {
      showAlert("Print is not supported on this device!", 'error');
      return;
    }

    try {
      setLoader(true);
      
      // Store original body content and styles
      const originalBody = document.body.innerHTML;
      const originalTitle = document.title;
      
      // Create print-specific content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Lesson Plan</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              margin: 0;
              padding: 20px;
              background-color: white;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #2c3e50;
              margin-top: 20px;
              margin-bottom: 10px;
              font-weight: bold;
            }
            h1 {
              font-size: 24px;
              padding-bottom: 10px;
            }
            h2 {
              font-size: 20px;
            }
            h3 {
              font-size: 18px;
            }
            p {
              margin-bottom: 10px;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            ul, ol {
              margin-bottom: 10px;
              padding-left: 20px;
              word-wrap: break-word;
            }
            li {
              margin-bottom: 5px;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              max-width: 100%;
              margin-bottom: 20px;
              table-layout: fixed;
              word-wrap: break-word;
            }
            table, th, td {
              border: 1px solid #ddd;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            th, td {
              padding: 8px;
              text-align: left;
            }
            .se-custom-tag {
              background-color: #f0f0f0;
              padding: 2px 0px;
              border-radius: 3px;
              font-weight: bold;
            }
            strong, b {
              font-weight: bold;
            }
            em, i {
              font-style: italic;
            }
            u {
              text-decoration: underline;
            }
            blockquote {
              border-left: 4px solid #ddd;
              margin: 10px 0;
              padding-left: 15px;
              font-style: italic;
            }
            @media print {
              @page {
                size: letter;
                margin: 0.5in;
              }
              body {
                margin: 0;
                padding: 20px;
              }
              * {
                page-break-inside: avoid;
              }
              h1, h2, h3, h4, h5, h6 {
                page-break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${contentToPrint || '<p>No content available</p>'}
        </body>
        </html>
      `;
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
        setLoader(false);
      };

    } catch (error) {
      console.error("Error printing HTML content:", error);
      showAlert("Something went wrong while printing!", 'error');
      setLoader(false);
    }
  };

  const handleModifiedChange = () => {
    const isModified = typeof hasModifiedChange === 'function' ? hasModifiedChange() : hasModifiedChange;
    if (isModified) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to print the lesson plan?',
        okText: 'Update and Print',
        cancelText: 'Cancel',
        zIndex: 3000,
        onOk: async () => {
          if (updateContent) await updateContent();
          const contentAfterUpdate = typeof getPrintContent === 'function' ? getPrintContent() : undefined;
          printHTMLContent(contentAfterUpdate);
        },
      });
      return;
    } else {
      printHTMLContent();
    }
  };

  return (
    <Button 
      variant="contained"
      color="secondary"
      startIcon={loader || printLoader ? <Loading size={20} /> : <Print />} 
      onClick={e => {
        e.preventDefault();
        if (isMobile || isTablet) {
          showAlert("Print is not supported on this device!", 'error');
          return;
        }
        handleModifiedChange();
      }}   
      size="small"
      style={{
        backgroundColor: '#8e24aa',
        color: 'white',
        fontSize: '13px',
        padding: '4px 8px',
        minWidth: 'auto',
        height: '36px',
      }}
    >
      Print
    </Button>
  );
}
